import { supabase } from "@/integrations/supabase/client";
import { emailValida, passwordDaImpostare } from "../../supabase/functions/_shared/installatore";

/**
 * L'accesso in due passi e il codice via email (06/10/2026): il lato app della
 * funzione `accesso-codice`. Le regole e il perche' stanno li' e in
 * supabase/functions/_shared/installatore.ts.
 */

export type EsitoVerifica =
  /** Account con password: si chiede la password (o il codice). */
  | { tipo: "password" }
  /** Creato da Datacom, password non ancora scelta: si chiede il codice arrivato per email. */
  | { tipo: "codice" }
  /** Nessun account con questa email. */
  | { tipo: "nessuno" }
  /** La verifica non ha risposto: si fa come prima, con la password. */
  | { tipo: "sconosciuto" };

export async function verificaEmail(email: string): Promise<EsitoVerifica> {
  try {
    const { data, error } = await supabase.functions.invoke("accesso-codice", {
      body: { azione: "verifica", email: email.trim() },
    });
    if (error || typeof data?.esiste !== "boolean") return { tipo: "sconosciuto" };
    if (!data.esiste) return { tipo: "nessuno" };
    return data.nuovo === true ? { tipo: "codice" } : { tipo: "password" };
  } catch {
    return { tipo: "sconosciuto" };
  }
}

/** Il messaggio d'errore scritto dalla funzione, non «non-2xx status code». */
async function erroreDellaFunzione(error: unknown, ripiego: string): Promise<string> {
  const contesto = (error as { context?: Response })?.context;
  if (contesto && typeof contesto.json === "function") {
    try {
      const corpo = await contesto.json();
      if (corpo?.error) return String(corpo.error);
    } catch {
      // corpo non leggibile
    }
  }
  return ripiego;
}

/** Manda la mail col codice. Ritorna l'errore da mostrare, o null se e' partita. */
export async function mandaCodice(email: string): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke("accesso-codice", {
    body: { azione: "codice", email: email.trim() },
  });
  if (error) return erroreDellaFunzione(error, "Codice non inviato: riprova fra poco");
  if (data?.esiste === false) return "Con questa email non c'è un account";
  return null;
}

/** Solo le cifre: chi incolla il codice dalla mail si porta dietro spazi e a capo. */
export function pulisciCodice(grezzo: string): string {
  return (grezzo ?? "").replace(/\D/g, "").slice(0, 10);
}

/** Supabase manda codici da 6 cifre (8 se cambiato nel pannello): sotto le 6 non si prova nemmeno. */
export function codiceCompleto(codice: string): boolean {
  return /^\d{6,10}$/.test(codice);
}

/**
 * Il pulsante della mail (linkAccesso in _shared/installatore.ts) porta qui con
 * email, codice e «benvenuto» dopo il «#». Si apre direttamente il passo del
 * codice, gia' scritto: entrare resta un clic su «Entra». null = niente link.
 */
export function datiDalLink(hash: string): { email: string; codice: string; benvenuto: boolean } | null {
  const p = new URLSearchParams((hash ?? "").replace(/^#/, ""));
  const email = (p.get("email") ?? "").trim();
  if (!emailValida(email)) return null;
  return { email, codice: pulisciCodice(p.get("codice") ?? ""), benvenuto: p.get("benvenuto") === "1" };
}

/**
 * Entra con email + codice. `type: "email"` accetta sia il codice di conferma
 * sia quello di recupero, che e' quello che mandiamo noi (vedi
 * _shared/codice_accesso.ts).
 */
export async function entraConCodice(email: string, codice: string): Promise<{ errore: string | null; daImpostare: boolean }> {
  const { data, error } = await supabase.auth.verifyOtp({ email: email.trim(), token: codice, type: "email" });
  if (error || !data.user) {
    const scaduto = /expired|invalid/i.test(error?.message ?? "");
    return { errore: scaduto ? "Codice sbagliato o scaduto: controlla la mail o chiedine uno nuovo" : "Accesso non riuscito: riprova", daImpostare: false };
  }
  return { errore: null, daImpostare: passwordDaImpostare(data.user.user_metadata) };
}

export { passwordDaImpostare };
